import { HIERARCHY_LABELS, ZONE_LABEL, ZONE_LABEL_PLURAL } from './hierarchyLabels';

/*
 * SCOPE-MULTI01 (ТЗ v4 §21–22, FE-FIX18/FE-FIX19).
 *
 * Чистая логика multi-select scope: разбор/сборка URL, canonical query для
 * backend и подписи. Компонент ScopeFilter и страницы («Сводка», «Контроль»)
 * хранят выбор в этой форме, но каждая — в своём URL, поэтому scope одной
 * страницы никогда не перетирает scope другой.
 *
 * Ключевые инварианты контракта:
 *  - «Все» = отсутствие narrowing, то есть пустой массив, а НЕ перечисление
 *    всех доступных ID. Пустой уровень не уходит в query вообще.
 *  - union внутри уровня и intersection между уровнями считает backend;
 *    здесь нет ни одной попытки пересечь выбор самостоятельно.
 *  - ID внутри фронта — строки (URL и DOM всё равно строковые); числовыми они
 *    становятся только в момент отправки, потому что backend ждёт integer.
 */

export const SCOPE_LEVELS = [
  { key: 'factoryIds', param: 'factory_ids', legacyParam: 'factory_id', labels: HIERARCHY_LABELS.factory, numeric: true },
  { key: 'workshopIds', param: 'workshop_ids', labels: HIERARCHY_LABELS.workshop, numeric: true },
  { key: 'sectionIds', param: 'section_ids', labels: HIERARCHY_LABELS.section, numeric: true },
  { key: 'roomIds', param: 'room_ids', labels: HIERARCHY_LABELS.room, numeric: true },
  { key: 'cameraIds', param: 'camera_ids', labels: HIERARCHY_LABELS.camera, numeric: true },
  /*
   * FILTER-UX01 (ТЗ v4 §19.1, FE-FIX16): «Зона контроля (где применимо)».
   *
   * Уровень показывается только экранам, которым зоны нужны (Ганта), поэтому
   * для «Сводки» и «Контроля» он всегда пустой и в их запросы не попадает.
   *
   * ID зоны уникален внутри камеры, а не глобально, поэтому здесь он хранится
   * как `${cameraId}:${zoneId}`: иначе одноимённая зона одной камеры включала
   * бы панель у другой. Разбирает пару тот, кто строит запрос (zone_id уходит
   * в backend уже без префикса).
   */
  {
    key: 'zoneIds',
    param: 'zone_ids',
    labels: {
      one: ZONE_LABEL,
      plural: ZONE_LABEL_PLURAL,
      all: 'Все зоны контроля',
      accusative: 'зону контроля',
      genitivePlural: 'зон контроля',
    },
    numeric: false,
  },
  {
    key: 'ruleIds',
    param: 'rule_ids',
    labels: { one: 'Правило', plural: 'Правила', all: 'Все правила', genitivePlural: 'правил' },
    numeric: false,
  },
];

export const SCOPE_LEVEL_KEYS = SCOPE_LEVELS.map((level) => level.key);

export const createEmptyScope = () => Object.fromEntries(SCOPE_LEVEL_KEYS.map((key) => [key, []]));

export const EMPTY_SCOPE = createEmptyScope();

const levelByKey = Object.fromEntries(SCOPE_LEVELS.map((level) => [level.key, level]));

/** Строки без пустышек и дублей; порядок выбора сохраняется. */
const normalizeIds = (values) => {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

export const normalizeScope = (scope) =>
  Object.fromEntries(SCOPE_LEVEL_KEYS.map((key) => [key, normalizeIds(scope?.[key])]));

export const isEmptyScope = (scope) => SCOPE_LEVEL_KEYS.every((key) => normalizeIds(scope?.[key]).length === 0);

export const countScopeSelection = (scope) =>
  SCOPE_LEVEL_KEYS.reduce((total, key) => total + normalizeIds(scope?.[key]).length, 0);

/**
 * URL → scope. Понимаются и повторяющиеся параметры
 * (?camera_ids=1&camera_ids=2), и список через запятую: первый вид пишем сами,
 * второй встречается в ссылках, которые правят руками.
 *
 * legacy factory_id читается как выбор одного объекта — старые ссылки и
 * закладки продолжают работать (ТЗ §21: legacy остаётся совместимым).
 */
export const parseScopeParams = (searchParams) => {
  const scope = createEmptyScope();
  if (!searchParams) return scope;
  SCOPE_LEVELS.forEach((level) => {
    const raw = searchParams.getAll(level.param).flatMap((value) => String(value).split(','));
    if (raw.length === 0 && level.legacyParam) {
      raw.push(...searchParams.getAll(level.legacyParam));
    }
    scope[level.key] = normalizeIds(raw);
  });
  return scope;
};

/**
 * scope → URL. Возвращает новый URLSearchParams: прежние scope-параметры
 * (включая legacy factory_id) удаляются целиком, чтобы в адресе не оставалось
 * следов предыдущего выбора.
 */
export const applyScopeParams = (searchParams, scope) => {
  const next = new URLSearchParams(searchParams || '');
  SCOPE_LEVELS.forEach((level) => {
    next.delete(level.param);
    if (level.legacyParam) next.delete(level.legacyParam);
  });
  SCOPE_LEVELS.forEach((level) => {
    normalizeIds(scope?.[level.key]).forEach((id) => next.append(level.param, id));
  });
  return next;
};

/** scope → query-параметры backend. Пустой уровень не отправляется. */
export const toScopeQuery = (scope) => {
  const query = {};
  SCOPE_LEVELS.forEach((level) => {
    const ids = normalizeIds(scope?.[level.key]);
    if (ids.length === 0) return;
    query[level.param] = level.numeric ? ids.map(Number).filter(Number.isFinite) : ids;
  });
  return query;
};

/**
 * Переключение одного чекбокса. Дочерние уровни здесь не трогаются: их
 * подрезает pruneScopeLevel, когда придёт новый список доступных опций.
 */
export const toggleScopeId = (scope, levelKey, id) => {
  const next = normalizeScope(scope);
  const value = String(id);
  next[levelKey] = next[levelKey].includes(value)
    ? next[levelKey].filter((item) => item !== value)
    : [...next[levelKey], value];
  return next;
};

export const setScopeLevel = (scope, levelKey, ids) => {
  const next = normalizeScope(scope);
  next[levelKey] = normalizeIds(ids);
  return next;
};

/** Сброс уровня и всех его потомков — «Все доступные» ниже этой точки. */
export const clearScopeFrom = (scope, levelKey) => {
  const next = normalizeScope(scope);
  const start = SCOPE_LEVEL_KEYS.indexOf(levelKey);
  if (start < 0) return next;
  SCOPE_LEVEL_KEYS.slice(start).forEach((key) => { next[key] = []; });
  return next;
};

/**
 * Выбранные ID уровня, которых больше нет среди доступных опций (родителя
 * сняли), выбрасываются. Возвращается тот же объект, если ничего не изменилось,
 * — иначе effect в компоненте зацикливался бы на новой ссылке.
 */
export const pruneScopeLevel = (scope, levelKey, availableIds) => {
  const current = normalizeIds(scope?.[levelKey]);
  if (current.length === 0) return scope;
  const allowed = new Set(normalizeIds(availableIds));
  const kept = current.filter((id) => allowed.has(id));
  if (kept.length === current.length) return scope;
  return { ...normalizeScope(scope), [levelKey]: kept };
};

const plural = (count, forms) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

const LEVEL_COUNT_FORMS = {
  factoryIds: ['объект', 'объекта', 'объектов'],
  workshopIds: ['подразделение', 'подразделения', 'подразделений'],
  sectionIds: ['область', 'области', 'областей'],
  roomIds: ['локация', 'локации', 'локаций'],
  cameraIds: ['камера', 'камеры', 'камер'],
  zoneIds: ['зона контроля', 'зоны контроля', 'зон контроля'],
  ruleIds: ['правило', 'правила', 'правил'],
};

export const ALL_SCOPE_LABEL = 'Все доступные';

/** Короткая сводка выбора для строки фильтра: «2 объекта · 3 камеры». */
export const describeScopeSelection = (scope) => {
  const parts = SCOPE_LEVEL_KEYS
    .map((key) => {
      const count = normalizeIds(scope?.[key]).length;
      return count === 0 ? '' : `${count} ${plural(count, LEVEL_COUNT_FORMS[key])}`;
    })
    .filter(Boolean);
  return parts.length === 0 ? ALL_SCOPE_LABEL : parts.join(' · ');
};

/**
 * Сравнение UI-выбора с scope.selection из подтверждённого ответа (ТЗ §21 DoD).
 * Нужно, чтобы не выдавать данные одного scope за другой: пока ответ относится
 * к прежнему выбору, страница показывает его как stale, а не как актуальный.
 */
export const scopeMatchesSelection = (scope, selection) => {
  if (!selection) return isEmptyScope(scope);
  return SCOPE_LEVELS.every((level) => {
    const ui = normalizeIds(scope?.[level.key]).slice().sort();
    const applied = normalizeIds(selection?.[level.param]).slice().sort();
    return ui.length === applied.length && ui.every((id, index) => id === applied[index]);
  });
};

/*
 * Пара «камера + зона» в одном ID уровня zoneIds (см. определение уровня).
 * zone_id по контракту строка и может содержать двоеточие, поэтому режем
 * только первый разделитель.
 */
export const encodeZoneOptionId = (cameraId, zoneId) => `${cameraId}:${zoneId}`;

export const decodeZoneOptionId = (value) => {
  const raw = value == null ? '' : String(value);
  const index = raw.indexOf(':');
  if (index < 0) return { cameraId: '', zoneId: raw };
  return { cameraId: raw.slice(0, index), zoneId: raw.slice(index + 1) };
};

export const getScopeLevel = (levelKey) => levelByKey[levelKey];
